<template>
    <Form :validation-schema="schema" v-slot="{ handleSubmit, errors }" as="div">
        <div class="card">
            <div class="card-header">
                <span class="card-title p-0 m-0">General Information</span>
            </div>
            <div class="card-body">
                <div class="row g-3">
                    <div class="col-md-6">
                        <label for="name" class="form-label">Project Name <span class="text-danger">*</span></label>
                        <Field
                            type="text"
                            class="form-control"
                            name="name"
                            placeholder="Enter project name"
                            v-model="project.name"
                            :class="{ 'is-invalid': errors.name }"
                        ></Field>
                        <div v-if="errors.name" class="is-invalid invalid-feedback">{{ errors.name }}</div>
                    </div>
                    <div class="col-md-6">
                        <label for="customer" class="form-label">Client Name <span class="text-danger">*</span></label>
                        <Field
                            name="customer"
                            :value="project.customer"
                            v-slot="{ field }"
                        >
                            <vue-multiselect
                                v-bind="field"
                                v-model="project.customer_select"
                                :class="{ 'is-invalid': errors.customer }"
                                :options="customerOptions"
                                placeholder="Search or add new customer"
                                tag-placeholder="Add this as new customer"
                                :taggable="true"
                                :show-labels="false"
                                track-by="name_with_address"
                                label="name_with_address"
                                @tag="addCustomer"
                                @select="selectCustomer"
                            ></vue-multiselect>
                        </Field>
                        <div v-if="errors.customer" class="is-invalid invalid-feedback">{{ errors.customer }}</div>
                    </div>
                    <div class="col-md-6">
                        <label for="email" class="form-label">Email <span class="text-danger">*</span></label>
                        <Field
                            type="email"
                            class="form-control"
                            name="email"
                            placeholder="Enter email"
                            v-model="project.email"
                            :class="{ 'is-invalid': errors.email }"
                        ></Field>
                        <div v-if="errors.email" class="is-invalid invalid-feedback">{{ errors.email }}</div>
                    </div>
                    <div class="col-md-6">
                        <label for="phone" class="form-label">Phone <span class="text-danger">*</span></label>
                        <Field
                            type="phone"
                            class="form-control"
                            name="phone"
                            placeholder="Enter phone"
                            v-model="project.phone"
                            :class="{ 'is-invalid': errors.phone }"
                        ></Field>
                        <div v-if="errors.phone" class="is-invalid invalid-feedback">{{ errors.phone }}</div>
                    </div>
                    <div class="col-md-6">
                        <label for="address" class="form-label">Address <span class="text-danger">*</span></label>
                        <Field
                            type="text"
                            class="form-control"
                            name="address"
                            placeholder="Enter address"
                            v-model="project.address"
                            :class="{ 'is-invalid': errors.address }"
                        ></Field>
                        <div v-if="errors.address" class="is-invalid invalid-feedback">{{ errors.address }}</div>
                    </div>
                    <div class="col-md-6">
                        <label for="address2" class="form-label">Address Two</label>
                        <Field
                            type="text"
                            class="form-control"
                            name="address2"
                            placeholder="Enter address two"
                            v-model="project.address2"
                            :class="{ 'is-invalid': errors.address2 }"
                        ></Field>
                        <div v-if="errors.address2" class="is-invalid invalid-feedback">{{ errors.address2 }}</div>
                    </div>
                    <div class="col-md-6">
                        <label for="city" class="form-label">City <span class="text-danger">*</span></label>
                        <Field
                            type="text"
                            class="form-control"
                            name="city"
                            placeholder="Enter city"
                            v-model="project.city"
                            :class="{ 'is-invalid': errors.city }"
                        ></Field>
                        <div v-if="errors.city" class="is-invalid invalid-feedback">{{ errors.city }}</div>
                    </div>
                    <div class="col-md-6">
                        <label for="state" class="form-label">State <span class="text-danger">*</span></label>
                        <Field
                            type="text"
                            class="form-control"
                            name="state"
                            placeholder="Enter state"
                            v-model="project.state"
                            :class="{ 'is-invalid': errors.state }"
                        ></Field>
                        <div v-if="errors.state" class="is-invalid invalid-feedback">{{ errors.state }}</div>
                    </div>
                    <div class="col-md-6">
                        <label for="postal_code" class="form-label">Postal Code <span class="text-danger">*</span></label>
                        <Field
                            type="text"
                            class="form-control"
                            name="postal_code"
                            placeholder="Enter postal code"
                            v-model="project.postal_code"
                            :class="{ 'is-invalid': errors.postal_code }"
                        ></Field>
                        <div v-if="errors.postal_code" class="is-invalid invalid-feedback">{{ errors.postal_code }}</div>
                    </div>
                    <div class="col-md-3">
                        <label for="amount" class="form-label">Expected Amount <span class="text-danger">*</span></label>
                        <Field
                            type="text"
                            class="form-control"
                            name="amount"
                            placeholder="Enter expected amount"
                            v-model="project.amount"
                            :class="{ 'is-invalid': errors.amount }"
                        ></Field>
                        <div v-if="errors.amount" class="is-invalid invalid-feedback">{{ errors.amount }}</div>
                    </div>
                    <div class="col-md-3">
                        <label for="avg_staff_cost" class="form-label">Average Staff Cost</label>
                        <Field
                            type="text"
                            class="form-control"
                            name="avg_staff_cost"
                            placeholder="Enter average staff cost"
                            v-model="project.avg_staff_cost"
                            :class="{ 'is-invalid': errors.avg_staff_cost }"
                        ></Field>
                        <div v-if="errors.avg_staff_cost" class="is-invalid invalid-feedback">{{ errors.avg_staff_cost }}</div>
                    </div>
                    <div class="col-md-6">
                        <label for="start_date" class="form-label">Start Date <span class="text-danger">*</span></label>
                        <Field
                            class="form-control"
                            name="start_date"
                            :value="project.start_date"
                            v-slot="{ field }"
                        >
                            <VueDatePicker
                                v-bind="field"
                                v-model="project.start_date"
                                :enable-time-picker="false"
                                :format="'dd-MM-yyyy'"
                            >
                            </VueDatePicker>
                        </Field>
                        <div v-if="errors.start_date" class="is-invalid invalid-feedback">{{ errors.start_date }}</div>
                    </div>
                    <div class="col-md-6">
                        <label for="end_date" class="form-label">End Date <span class="text-danger">*</span></label>
                        <Field
                            class="form-control"
                            name="end_date"
                            :value="project.end_date"
                            v-slot="{ field, meta  }"
                        >
                            <VueDatePicker
                                v-bind="field"
                                v-model="project.end_date"
                                :enable-time-picker="false"
                                :format="'dd-MM-yyyy'"
                                :min-date="project.start_date"
                            >
                            </VueDatePicker>
                        </Field>
                        <div v-if="errors.end_date" class="is-invalid invalid-feedback">{{ errors.end_date }}</div>
                    </div>

                    <div class="col-12">
                        <button
                            type="button"
                            class="btn btn-theme float-end"
                            :class="{ 'disabled': processing }"
                            :disabled="processing"
                            v-text="processing ? 'Processing...' : 'Create'"
                            @click="handleSubmit($event, createProject)"
                        ></button>
                    </div>
                </div>
            </div>
        </div>
        <div class="card d-none">
            <div class="card-header">
                <span class="card-title p-0 m-0">Assign Users <button v-if="project.users.length <= staffUsers.length" type="button" class="btn btn-sm btn-theme float-end" @click="addUserToProject"><i class="bi bi-plus-circle"></i> Add More</button></span>
            </div>
            <div class="card-body">
                <div class="row g-3">
                    <template v-for="(user, index) in project.users">
                        <div class="col-md-4">
                            <label for="staff" class="form-label">Select User <span class="text-danger">*</span></label>
                            <Field
                                name="staff"
                                :value="user.id"
                                v-slot="{ field }"
                            >
                                <vue-multiselect
                                    v-bind="field"
                                    v-model="user.user_select"
                                    :options="staffUsers"
                                    placeholder="Select user"
                                    :taggable="false"
                                    :show-labels="false"
                                    track-by="name_with_email"
                                    label="name_with_email"
                                    @select="selectUser($event, index)"
                                ></vue-multiselect>
                            </Field>
                        </div>
                        <div class="col-md-4">
                            <label :for="'labour_cost' + index" class="form-label">Labour Cost <span class="text-danger">*</span></label>
                            <Field
                                type="number"
                                :name="'labour_cost' + index"
                                class="form-control"
                                placeholder="Enter labour cost"
                                v-model="user.labour_cost"
                            ></Field>
                        </div>
                        <div class="col-md-4">
                            <label for="work_date" class="form-label">Work Date <span class="text-danger">*</span></label>
                            <div class="d-flex align-items-center">
                                <VueDatePicker
                                    v-model="user.work_date"
                                    :enable-time-picker="false"
                                    :format="'dd-MM-yyyy'"
                                    :min-date="project.start_date"
                                    :max-date="project.end_date"
                                >
                                </VueDatePicker>
                                <button v-if="project.users.length > 1" type="button" class="btn btn-outline-danger ms-2" @click="removeUser(index)"><i class="bi bi-trash"></i></button>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <div class="row d-none">
            <div class="col-12">
                <button
                    type="button"
                    class="btn btn-theme float-end"
                    :class="{ 'disabled': processing }"
                    :disabled="processing"
                    v-text="processing ? 'Processing...' : 'Create'"
                    @click="handleSubmit($event, createProject)"
                ></button>
            </div>
        </div>
    </Form>
</template>
<script>
import {Field} from "vee-validate";
export default {
    name: 'CreateProject',
    components: {
        Field
    },
    data() {
        return {
            processing: false,
            customerOptions: [],
            project: {
                name: '',
                customer: '',
                email: '',
                phone: '',
                address: '',
                address2: '',
                city: '',
                county: '',
                state: '',
                postal_code: '',
                amount: '',
                avg_staff_cost: '',
                start_date: new Date(),
                end_date: new Date(new Date().setDate(new Date().getDate() + 5)),
                customer_select:null,
                users: [
                    {
                        id: '',
                        name: '',
                        email: '',
                        labour_cost: '',
                        user_select: null,
                        work_date: new Date(),
                    }
                ]
            },
            staffUsers: [],
        }
    },
    computed: {
        schema() {
            return {
                name: 'required',
                customer: 'required',
                email: 'required|email',
                phone: 'required|min:10|max:15|numeric',
                address: 'required',
                city: 'required',
                state: 'required',
                postal_code: 'required',
                amount: 'required',
                start_date: 'required',
                end_date: 'required',
            }
        }
    },
    mounted() {
        this.getStaffUsers();
        this.getUsersWithAddress();
    },
    methods: {
        getUsersWithAddress() {
            let self = this;
            axios.post('/admin/users/get-address-list', {})
                .then(response => {
                    self.customerOptions = [];
                    if (Object.keys(response.data.addresses).length > 0) {
                        response.data.addresses.forEach((user) => {
                            self.customerOptions.push({
                                name: user.customer_name,
                                email: user.customer_email,
                                phone: user.customer_phone_number,
                                name_with_address: user.name_with_address,
                                address: user.address,
                                city: user.city,
                                county: user.county,
                                state: user.state,
                                postal_code: user.postal_code,
                                address_id: user.id,
                                customer:user.user_id
                            });
                        });
                    }
                });
        },
        getStaffUsers() {
            let self = this;
            axios.post('/admin/staff/get-list')
                .then(response => {
                    self.staffUsers = [];

                    // Get id and name of users and push into the userOptions
                    response.data.users.forEach((user) => {
                        self.staffUsers.push({
                            id: user.id,
                            name: user.full_name,
                            email: user.email,
                            name_with_email: user.name_with_email
                        });
                    });
                });
        },
        selectCustomer(event) {
            this.project.customer = event.user_id;
            this.project.email = event.email;
            this.project.phone = event.phone;
            this.project.address = event.address;
            this.project.city = event.city;
            this.project.county = event.county;
            this.project.state = event.state;
            this.project.postal_code = event.postal_code;
        },
        addCustomer(newCustomer) {
            let tag = {
                name: newCustomer,
                email: '',
                phone: '',
                name_with_address: newCustomer,
                address: '',
                city: '',
                county: '',
                state: '',
                postal_code: '',
                address_id: '',
                customer: ''
            };

            this.customerOptions.push(tag);
            this.project.customer_select = tag;
        },
        createProject() {
            this.processing = true;
            let self = this;
            axios.post('/admin/projects', self.project)
                .then(response => {
                    if (response.data.status == 'success') {
                        // Display success message
                        self.$notification(response.data.status, response.data.message);
                        window.location.href = response.data.redirect_url;
                    }
                });
        },
        addUserToProject() {
            this.project.users.push({
                id: '',
                name: '',
                email: '',
                labour_cost: '',
                user_select: null,
                work_date: new Date(),
            });
        },
        removeUser(index) {
            this.project.users.splice(index, 1);
        },
        selectUser(event, index) {
            this.project.users[index].id = event.id;
            this.project.users[index].name = event.name;
            this.project.users[index].email = event.email;
        }
    }
}
</script>
